// src/components/AddEditAutoImportConfigurationsModal.tsx
import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { LTabStrip, LFade, LDialog } from "@loffa/gpp-modules";
import { GeneralInfoForm } from "./ModalFormSteps/GeneralInfoForm";
import { SourceFileForm } from "./ModalFormSteps/SourceFileForm";
import { Confirmation } from "./ModalFormSteps/Confirmation/Confirmation";
import styles from "./AddEditAutoImportConfigurationsModal.module.css";
import { useAutoImportFormContext } from "../Context/AutoImportFormContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAsterisk, faCheck } from "@fortawesome/sharp-solid-svg-icons";
import { validateGeneralInfo, validateSourceFile } from "./utils/formValidations";
import { AuthContext } from "../../../../../../../context/AuthContext";
import { AutoImportConfigurationData } from "../AutoImportConfigurations";

interface AddEditAutoImportConfigurationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  initialData?: AutoImportConfigurationData | null;
}
interface Tab {
  title: string | ReactNode;
  body: ReactNode;
}

export function AddEditAutoImportConfigurationsModal({isOpen,onClose,onSave,initialData = null}: AddEditAutoImportConfigurationsModalProps) {
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const { formData, resetFormData, setFormData, setError, error } = useAutoImportFormContext();
  const authContext = useContext(AuthContext);
  const token: string = authContext.authState().accessToken.accessToken;

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData, setFormData]);


  const handleSave = useCallback(async () => {
    let reqUrl: string;
    if (initialData?.scheduledJobGuid) {
      reqUrl = `${process.env.REACT_APP_API_GATEWAY_BASE}/datamanager/api/Automation/${initialData.scheduledJobGuid}`;
    } else {
      reqUrl = `${process.env.REACT_APP_API_GATEWAY_BASE}/datamanager/api/Automation`;
    }

    const payload = {
      ...(initialData?.scheduledJobGuid && {
        scheduledJobGuid: initialData?.scheduledJobGuid,
      }),
      scheduledTime: formData.scheduledTime,
      scheduleType: formData.scheduleType,
      jobName: formData.jobName,
      clientGuid: formData.client?.clientValuePayload.clientGuid,
      productGuid: formData.productType?.productGuid,
      clientProductGuid: formData.clientProductGuid,
      productTypeGuid: formData.productType?.productTypeId,
      fileHasHeaderRow: formData.fileHasHeaderRow,
      ftpServer: formData.ftpServer,
      ftpPort: formData.ftpPort?.toString(),
      ftpUserName: formData.ftpUsername,
      ftpPassword: formData.ftpPassword,
      sftpEnabled: formData.sftpEnabled,
      expectedFileName: formData.expectedFileName,
      archivePath: formData.archivePath,
      isActive: formData.isActive,
      archiveAfterProcessing: formData.archiveAfterProcessing,
      forDTC: formData.forDtc,
      associatedInst: formData.associatedInst,
      jobData: "-",
      sourceFileType: formData.sourceFileType,
      blobStorageFolder: formData.blobStorageFolder,
      blobStorageUsername: formData.blobStorageUsername,
      blobStoragePassword: formData.blobStoragePassword
    };

    try {
      const response = await fetch(reqUrl, {
        method: !initialData ? "POST" : "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Failed to save auto import configurations.");
    } catch (err: any) {
      setError("Error saving data: " + err.message);
    } finally {
      resetFormData();
      onSave();
    }
  }, [
    initialData,
    formData,
    resetFormData,
    setError,
    onSave,
    token,
  ]);

  const handleClose = () => {
    resetFormData();
    onClose();
  };

  const getTabTitle = useCallback(
    (baseTitle: string, isComplete: boolean) => (
      <span className={styles.tabTitle}>
        {baseTitle}{" "}
        {isComplete ? (
          <FontAwesomeIcon
            icon={faCheck}
            color="green"
            fontSize={8}
            className={styles.tabTitleIcon}
          />
        ) : (
          <FontAwesomeIcon
            icon={faAsterisk}
            color="red"
            fontSize={6}
            className={styles.tabTitleIcon}
          />
        )}
      </span>
    ),
    []
  );

  const isGeneralComplete = useMemo(() => validateGeneralInfo(formData), [formData]);
  const isSourceFileComplete = useMemo(() => validateSourceFile(formData), [formData]);

  const tabs: Tab[] = useMemo(
    () => [
      {
        title: getTabTitle("General", isGeneralComplete),
        body: <GeneralInfoForm />,
      },
      {
        title: getTabTitle("Source File", isSourceFileComplete),
        body: <SourceFileForm />,
      },
      {
        title: <b>Confirm & Save</b>,
        body: <Confirmation onSave={handleSave} />,
      },
    ],
    [getTabTitle, isGeneralComplete, isSourceFileComplete, handleSave]
  );

  if (!isOpen) return null;

  const modalBody = (
    <div className={styles.autoImportConfigModalContent}>
      {error && <div className={styles.error}>{error}</div>}
      <LTabStrip
        selected={selectedTab}
        onSelect={(e: { selected: number }) => setSelectedTab(e.selected)}
        orientation="vertical"
        tabs={tabs}
        tabPosition="top"
        key={selectedTab}
      />
    </div>
  );
  

  return (
    <LFade transitionEnterDuration={300}>
      <LDialog
        width="950"
        title={`${initialData ? "Edit" : "Add"} Auto Import Configuration`}
        subtitle={<div className={styles.modalSubtitle}>Required Fields</div>}
        body={modalBody}
        actionLayout="end"
        actionCloseButton={handleClose}
        actionOverlay={handleClose}
      />
    </LFade>
  );
}
